import { ApolloLink, DefaultContext, Observable } from '@apollo/client';
import { HTTP_HEADERS } from '@shared/constants';
import { idempotentOperations } from '../constants';

// Matches the backend's suggested dedup TTL (see the idempotency-key API contract doc),
// so client and server treat the same attempt as stale at the same time.
const ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredAttempt {
  key: string;
  createdAt: number;
}

// Non-cryptographic, synchronous — crypto.subtle.digest is async and doesn't fit the
// link's request path. Namespaced by operation name below, so a collision can only ever
// happen between two different payloads of the *same* mutation.
function shortHash(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

function buildStorageKey(operationName: string, variables: Record<string, unknown>): string {
  return `idempotency:${operationName}:${shortHash(JSON.stringify(variables))}`;
}

function readAttempt(storageKey: string): StoredAttempt | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed: StoredAttempt = JSON.parse(raw);
    if (Date.now() - parsed.createdAt >= ATTEMPT_TTL_MS) return null;

    return parsed;
  } catch {
    return null;
  }
}

function writeAttempt(storageKey: string, attempt: StoredAttempt): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(attempt));
  } catch {
    // private mode / quota exceeded — this call proceeds without persistence
  }
}

function clearAttempt(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export const idempotencyLink = new ApolloLink((operation, forward) => {
  const { operationName, variables } = operation;

  if (!idempotentOperations.includes(operationName)) {
    return forward(operation);
  }

  const storageKey = buildStorageKey(operationName, variables);
  const existing = readAttempt(storageKey);
  const key = existing?.key ?? crypto.randomUUID();

  // Only stamp a fresh createdAt when starting a new attempt — reusing an existing,
  // still-valid one must not keep pushing its TTL out on every retry.
  if (!existing) {
    writeAttempt(storageKey, { key, createdAt: Date.now() });
  }

  operation.setContext(({ headers }: DefaultContext) => ({
    headers: { ...headers, [HTTP_HEADERS.IDEMPOTENCY_KEY]: key },
  }));

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next(result) {
        // Reached the server, either succeeded or failed on business logic — either
        // way this attempt is resolved, so the next click is a new attempt.
        clearAttempt(storageKey);
        observer.next(result);
      },
      error(networkError) {
        observer.error(networkError);
      },
      complete() {
        observer.complete();
      },
    });

    return () => subscription.unsubscribe();
  });
});
